import { useState, useEffect } from "react";
import { CSS } from "./App";

// /reset-password#<token> is opened from the "Reset your CamStorage password" email — no login.
// The token is after the # so it never reaches a server log. Nothing changes until a new password is saved here.
export function ResetPasswordPage() {
  const [token] = useState(() => window.location.hash.slice(1));
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [linkState, setLinkState] = useState(token ? "checking" : "missing"); // checking | ok | expired | missing

  // Check the link as soon as the page opens, so a used or expired one says so before anything is typed.
  useEffect(() => {
    if (!token) return;
    fetch("/api/admin", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "checkReset", token }),
    })
      .then(r => r.json().catch(() => ({})).then(d => setLinkState(r.ok && d.ok ? "ok" : d.expired ? "expired" : "ok")))
      .catch(() => setLinkState("ok")); // can't check right now: show the form, Save checks again
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { setError("The two passwords don't match"); return; }
    setSaving(true);
    try {
      const r = await fetch("/api/admin", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "confirmReset", token, password: pw }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) throw new Error(d.error || "Could not change the password — please try again");
      window.history.replaceState(null, "", "/reset-password");
      setDone(true);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  }

  return (
    <div className="login-page">
      <style>{CSS}</style>
      <div className="login-box">
        <div className="login-logo" style={{ flexDirection: "column", gap: 8 }}>
          <div className="login-logotext" style={{ color: "var(--navy)" }}>CamStorage</div>
        </div>

        {done ? (
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <h3 style={{ color: "var(--navy)", fontFamily: "var(--fh)", marginBottom: 8 }}>Password changed</h3>
            <p style={{ fontSize: 13, color: "var(--sub)", marginBottom: 20 }}>Sign in with your new password. If you use an authenticator app, you'll still be asked for your 6-digit code.</p>
            <a className="login-btn" href="/" style={{ display: "block", textAlign: "center", textDecoration: "none" }}>Go to sign in</a>
          </div>
        ) : linkState === "missing" || linkState === "expired" ? (
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <p style={{ fontSize: 13, color: "var(--sub)", marginBottom: 20 }}>
              {linkState === "missing"
                ? "This reset link is incomplete. Please open the link from your email again, or ask for a new one."
                : "This reset link has expired or has already been used. You can ask for a new one from the sign-in page (\"Forgot your password?\")."}
            </p>
            <a className="login-btn" href="/" style={{ display: "block", textAlign: "center", textDecoration: "none" }}>Back to sign in</a>
          </div>
        ) : linkState === "checking" ? (
          <p className="login-sub" style={{ textAlign: "center", padding: "20px 0" }}>Checking your link…</p>
        ) : (
          <>
            <p className="login-sub">Choose a new password</p>
            {error && <div className="login-err">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="login-field">
                <label>New password</label>
                <input type="password" autoComplete="new-password" value={pw} onChange={e => setPw(e.target.value)} placeholder="At least 8 characters" required autoFocus />
              </div>
              <div className="login-field">
                <label>Confirm new password</label>
                <input type="password" autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat new password" required />
              </div>
              <button className="login-btn" type="submit" disabled={saving}>{saving ? "Saving..." : "Save new password"}</button>
            </form>
            <a href="/" style={{ display: "block", textAlign: "center", color: "var(--sub)", fontSize: 13, padding: "10px", marginTop: 4 }}>Back to sign in</a>
          </>
        )}
      </div>
    </div>
  );
}
